import dayjs, { Dayjs } from "dayjs";
import { Image as AntImage, Row, Col, Table, FormInstance, Form, Input, Button, Image } from "antd";
import { FileImageOutlined } from "@ant-design/icons";
import numeral from "numeral";
import useModal from "antd/es/modal/useModal";
import { useEffect, useMemo } from "react";
import { ColumnProps } from "antd/es/table";
import Slider from "react-slick";
import { useBidLogs } from "../../hooks/apis/bids";
import { usePagination } from "../../hooks/usePagintation";

import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

type BidFormProps = {
  form: FormInstance;
  car: {
    bookingTimes: Dayjs[];
    id: number;
    title: string;
    imageUrl: CarImageProps[];
    brand: string;
    modal: string;
    price: number;
    type: string;
    years: number;
    coordinatorName?: string;
    createdAt: number;
    location?: string;
    merchantId: number;
    name: string;
    phone: string;
    salerId?: string;
    status: "PENDING" | "CONCATED" | "DONE";
    viewingType: "ONLINE" | "OFFLINE";

    color: string;
    kilometers: number;
    basePrice: number;
    customerName: string;
    customerEmail: string;
    customerPhone: string;
    markTravelLicenseImageUrl: string;
    markAppraisalImageUrl?: string;
  };
};

type BidLogsProps = {
  bidder: { email: string; displayName: string };
  createTime: number;
  price: number;
};

export function BidForm(props: BidFormProps) {
  const [modal, contextHolder] = useModal();
  const pagination = usePagination({
    defaultPageSize: 30,
  });

  const { data: { logs = [] } = {}, isLoading: isBidLogsLoading } = useBidLogs({
    take: pagination.take,
    skip: pagination.skip,
    customerCarId: props.car.id,
  });

  useEffect(() => {
    props.form.resetFields();
  }, [props.form]);

  const showModalImage = ({ title, url = "" }: { title: string; url?: string }) => {
    if (url.substring(url.length - 4) === ".pdf") {
      window.open(url);
    } else {
      return modal.info({
        width: 800,
        title,
        content: <AntImage className="w-full mb-2" style={{ aspectRatio: "1 / 1", objectFit: "cover" }} src={url} />,
        icon: null,
        okText: "關閉",
      });
    }
  };

  const columns: ColumnProps<BidLogsProps>[] = [
    {
      width: 150,
      dataIndex: "createTime",
      title: "喊價時間",
      render: (createTime: number) => dayjs(createTime).format("YYYY-MM-DD HH:mm"),
    },
    {
      width: 100,
      dataIndex: "price",
      title: "喊價價錢",
      render: (price: number) => numeral(price).format("$0,0"),
    },
    {
      width: 80,
      dataIndex: "isSelfBid",
      title: "喊價商戶",
      render: (isSelfBid: number) => (isSelfBid ? "本商戶" : "-"),
    },
  ];

  return (
    <Row className="w-full" gutter={15}>
      {contextHolder}

      <Col span={12}>
        <CustomSlider images={props.car.imageUrl || []} />

        <div>
          <span className="text-m font-bold">標題：</span>
          {props.car?.title}
        </div>
        <div>
          <span className="text-m font-bold">車廠：</span>
          {props.car?.brand}
        </div>
        <div>
          <span className="text-m font-bold">車款：</span>
          {props.car?.type}
        </div>
        <div>
          <span className="text-m font-bold">車色：</span>
          {props.car?.color}
        </div>
        <div>
          <span className="text-m font-bold">里程：</span>
          {numeral(props.car?.kilometers).format("0,0")}
        </div>
        <div>
          <span className="text-m font-bold">出廠年份：</span>
          {props.car?.years}
        </div>
        {/* <div>
          <span className="text-m font-bold">車種：</span>
          {props.car?.modal}
        </div> */}
        <div>
          <span className="text-m font-bold">行照：</span>
          {props.car?.markTravelLicenseImageUrl ? (
            <Button
              type="primary"
              shape="circle"
              icon={<FileImageOutlined />}
              onClick={() => showModalImage({ title: "行照", url: props.car?.markTravelLicenseImageUrl })}
            />
          ) : (
            "無"
          )}
        </div>
        <div>
          <span className="text-m font-bold">車輛鑑定書：</span>
          {props.car?.markAppraisalImageUrl ? (
            <Button
              type="primary"
              shape="circle"
              icon={<FileImageOutlined />}
              onClick={() => showModalImage({ title: "車輛鑑定書", url: props.car?.markAppraisalImageUrl })}
            />
          ) : (
            "無"
          )}
        </div>
      </Col>
      <Col span={12}>
        <div className="text-lg font-bold">喊價紀錄</div>

        <Table
          dataSource={logs || []}
          columns={columns || []}
          loading={isBidLogsLoading}
          scroll={{ x: "max-content", y: "max-content" }}
          size="small"
          rowKey="createTime"
        ></Table>

        <div className="text-lg font-bold">競標</div>

        <Form className="bid-form" form={props.form}>
          <Form.Item
            label="喊價金額"
            name="price"
            rules={[
              {
                validator: async (rule, value) => {
                  if (value) {
                    value = String(Number(value));

                    if (value <= 0) {
                      throw new Error("價錢需大於1");
                    } else if (/^[0-9]+\.[0-9]+$/.test(value)) {
                      throw new Error("不允許小數");
                    } else if (!/^\d{1,9}$/.test(value)) {
                      throw new Error("位數需小於9位");
                    }
                  }
                },
              },
            ]}
          >
            <Input type="number" onWheel={e => e.currentTarget.blur()} />
          </Form.Item>
        </Form>
      </Col>
    </Row>
  );
}

type CarImageProps = { id: number; isCover: boolean; url: string };
type CustomSliderProps = {
  images: CarImageProps[];
};

const CustomSlider = (props: CustomSliderProps) => {
  const settings = useMemo(
    () => ({
      customPaging: (index: number) => (
        <div>
          <Image src={props.images[index].url} preview={false} alt="car-item" />
        </div>
      ),
      dots: true,
      dotsClass: "slick-dots slick-thumb",
      infinite: (props.images || []).length > 1 ? true : false,
      // speed: 500,
      // slidesToShow: 1,
      // slidesToScroll: 1,
    }),
    [props.images]
  );

  return (
    <div className="slider-container">
      <Slider {...settings}>
        {props.images.map((item, index) => (
          <div key={item.id}>
            <Image src={item.url} alt="car-item" />
          </div>
        ))}
      </Slider>
    </div>
  );
};
